import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { downloadValidooExportSummary } from './service';
import { ValidooPrepareSuccessResponse } from './types';
import ValidooExportCheckCard from './ValidooExportCheckCard';

interface ValidooExportSuccessDialogProps {
  open: boolean;
  result: ValidooPrepareSuccessResponse | null;
  onClose: () => void;
}

const ValidooExportSuccessDialog: React.FC<ValidooExportSuccessDialogProps> = ({
  open,
  result,
  onClose,
}) => {
  if (!result) return null;

  const handleDownloadSummary = () => {
    downloadValidooExportSummary(result);
  };
  const checks = result.preflight.checks || [];
  const passedCount = checks.filter((check) => check.status === 'pass').length;
  const failedCount = checks.filter((check) => check.status !== 'pass').length;
  const assetTitle = result.preflight.asset?.fileName || result.downloads.marketImage.filename;
  const assetSubtitle = `GTIN ${result.gtin}`;
  const metaParts = [
    result.preflight.asset?.width && result.preflight.asset?.height
      ? `Dimensioner: ${result.preflight.asset.width} x ${result.preflight.asset.height} px`
      : '',
    result.preflight.resolvedData?.icc?.sourceProfileName
      ? `ICC: ${result.preflight.resolvedData.icc.sourceProfileName}`
      : '',
    result.preflight.resolvedData?.icc?.sourceColorSpace
      ? `Färgrymd: ${result.preflight.resolvedData.icc.sourceColorSpace}`
      : '',
    result.preflight.resolvedData?.clippingPath?.name
      ? `Urklippsbana: ${result.preflight.resolvedData.clippingPath.name}`
      : '',
    `Marknadsbild: ${result.downloads.marketImage.filename}`,
    `Planogram: ${result.downloads.planogramImage.filename}`,
  ].filter(Boolean);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 28 }} />
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            Spara för Validoo
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 2.5 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
            mb: 2,
            p: 2,
            borderRadius: 2,
            backgroundColor: '#e8f5e9',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32' }}>
              Validoo-export godkänd
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Båda filerna har verifierats och skickats till webbläsaren för nedladdning.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip
              icon={<CheckCircleIcon />}
              label={`${passedCount} godkända`}
              size="small"
              sx={{ backgroundColor: '#c8e6c9', fontWeight: 600 }}
            />
            <Chip
              label={`${failedCount} underkända`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            <Chip
              label={`GTIN: ${result.gtin}`}
              size="small"
              variant="outlined"
            />
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#072f53' }}>
            Resultat (1 bild)
          </Typography>
        </Box>

        <Box sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
          <ValidooExportCheckCard
            title={assetTitle}
            subtitle={assetSubtitle}
            checks={checks}
            metaLine={metaParts.join(' • ')}
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Du kan också spara exportinformationen som en textfil om den ska skickas vidare till någon annan.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between' }}>
        <Button
          onClick={handleDownloadSummary}
          startIcon={<DownloadOutlinedIcon />}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Spara exportinfo
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ backgroundColor: '#072f53', '&:hover': { backgroundColor: '#0a3d6b' } }}
        >
          Stäng
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValidooExportSuccessDialog;
